import { alertStore } from '~/modules/common/alerter/alert-store';
import { boardStore } from '~/modules/common/board/board-store';
import { draftStore } from '~/modules/common/form-draft/draft-store';
import { navigationStore } from '~/modules/navigation/navigation-store';
import { seenStore } from '~/modules/seen/seen-store';
import { userStore } from '~/modules/user/user-store';
import { extraLocalUserStores } from '~/query/extra-local-user-stores';
import { bindLocalUserDb, closeLocalUserDb, deletedElsewhereListeners, notifyOwnerChange } from '~/query/local-user-db';
import { resetPersisters } from '~/query/persister';
import { syncStore } from '~/query/realtime/sync-store';

/** Minimal contract a per-user store must satisfy to join {@link localUserStores}: hydrate on bind, reset on sign-out. */
export interface LocalUserStore {
  persist: { rehydrate: () => void | Promise<void> };
  getState: () => { reset: () => void };
}

/** Persisted zustand stores living in `localUserDb.kv`, in-memory while signed out. Each exposes `reset()` for {@link unbind}; an app appends its own via {@link extraLocalUserStores}. */
const localUserStores = [seenStore, syncStore, navigationStore, draftStore, alertStore, boardStore, ...extraLocalUserStores];

let boundOwner: string | null = null;
let readyPromise: Promise<void> = Promise.resolve();

/** Owner to bind: the current user, unless impersonated (then ephemeral, no durable DB). */
function resolveOwner(): string | null {
  const { user, impersonator } = userStore.getState();
  return impersonator ? null : user?.id || null;
}

async function hydrateAll(): Promise<void> {
  await Promise.all(localUserStores.map((store) => store.persist.rehydrate()));
}

function bindOwner(ownerId: string): void {
  boundOwner = ownerId;
  bindLocalUserDb(ownerId);
  resetPersisters();
  readyPromise = hydrateAll();
}

function unbind(): void {
  boundOwner = null;
  closeLocalUserDb();
  resetPersisters();
  // The DB is already closed, so these resets no-op on persist.
  for (const store of localUserStores) store.getState().reset();
  readyPromise = Promise.resolve();
}

/** Reconcile the bound DB with the current auth state. Cheap; no-ops when unchanged. */
function reconcileOwner(): void {
  const owner = resolveOwner();
  if (owner === boundOwner) return;
  if (owner) bindOwner(owner);
  else unbind();
  notifyOwnerChange(boundOwner);
}

/** Resolves once `localUserDb` is open and all local user stores have rehydrated for the current owner. */
export function localUserStorageReady(): Promise<void> {
  return readyPromise;
}

userStore.subscribe(reconcileOwner);
reconcileOwner();

// Another tab's hard sign-out deleted this user's database: mirror it here, then leave the app. Lazy imports keep the
// router and teardown out of this module's importer graph.
deletedElsewhereListeners.add(() => {
  void import('~/utils/teardown-user-state')
    .then(({ teardownUserState }) => teardownUserState())
    .then(() => import('~/routes/router'))
    .then(({ router }) => router.navigate({ to: '/auth/authenticate', replace: true }));
});
